import React, { useEffect, useMemo, useState } from "react";
import { SectionTitle } from "./SectionTitle";
import {
    Box,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { CreditCard, Bank, HandCoins, CheckCircle } from "@phosphor-icons/react";

import { CheckoutFormTypeFields, useCheckoutForm } from "./hooks/useCheckoutForm";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";
import { textFieldStyle } from "./CouponStyles";
import { MP_LOGO_URL, PROVEEDOR_PAGO } from "./checkoutConstants";
import {
    cardBrandLabel,
    cardNumberMatchesSelectedBrand,
    detectCardBrand,
} from "@ecommerce/shared-utils";

import {
    CardTypeViewDTO,
    OrderPaymentData,
    OrderPaymentOptionViewDTO,
    OrderPaymentOptionViewDTOFields,
} from "../../types/purchaseOrder/Order";
import { STYLES } from "../ShoppingCart/ShoppingCartStyles";

const PROVIDER_ICON_CONFIG: Record<number, { icon: React.ReactNode; bg: string; color: string }> = {
    [PROVEEDOR_PAGO.MERCADO_PAGO]: { icon: null, bg: "#e8f4fb", color: "#009ee3" },
    [PROVEEDOR_PAGO.EFECTIVO]: {
        icon: <HandCoins size={20} weight="duotone" />,
        bg: "#f3f4f6",
        color: "#4b5563",
    },
    [PROVEEDOR_PAGO.TARJETA]: {
        icon: <CreditCard size={20} weight="duotone" />,
        bg: "#ede9fe",
        color: "#7c3aed",
    },
    [PROVEEDOR_PAGO.CBU]: {
        icon: <Bank size={20} weight="duotone" />,
        bg: "#d1fae5",
        color: "#059669",
    },
};

const isMercadoPagoProvider = (providerCode?: number | null) =>
    Number(providerCode) === PROVEEDOR_PAGO.MERCADO_PAGO;

export const PaymentOptionSection: React.FC = () => {
    const {
        confirmedZipCode,
        methods,
        paymentOptions,
        isLoadingPaymentOptions,
        paymentOptionId,
        setPaymentOptionId,
        setPaymentData,
        getPaymentData,
        selectedPaymentProviderCode,
        cardTypes,
        isLoadingCardTypes,
    } = useCheckoutForm();

    const [paymentData, setLocalPaymentData] = useState<OrderPaymentData | null>(() =>
        getPaymentData(),
    );

    useEffect(() => {
        setLocalPaymentData(null);
        setPaymentData(null);
    }, [paymentOptionId]);

    const handleSetPaymentData: React.Dispatch<React.SetStateAction<OrderPaymentData | null>> = (
        updater,
    ) => {
        setLocalPaymentData((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            setPaymentData(next);
            return next;
        });
    };

    useEffect(() => {
        methods.register(CheckoutFormTypeFields.PaymentOptionId, {
            required: "Seleccioná un método de pago.",
        });
    }, [methods]);

    const flatOptions = useMemo<OrderPaymentOptionViewDTO[]>(() => {
        const list = [...(paymentOptions ?? [])];

        list.sort((a: any, b: any) => {
            const aIsMp = isMercadoPagoProvider(a?.[OrderPaymentOptionViewDTOFields.ProviderCode]);
            const bIsMp = isMercadoPagoProvider(b?.[OrderPaymentOptionViewDTOFields.ProviderCode]);
            if (aIsMp === bIsMp) return 0;
            return aIsMp ? -1 : 1;
        });

        return list;
    }, [paymentOptions]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPaymentOptionId(e.target.value); // GUID
    };

    const errorMsg =
        (methods.formState.errors as any)?.[CheckoutFormTypeFields.PaymentOptionId]?.message ??
        null;

    if (!confirmedZipCode) return null;

    return (
        <Stack mb={2}>
            <SectionTitle>Método de pago</SectionTitle>

            {isLoadingPaymentOptions ? (
                <Typography sx={TYPOGRAPHY_STYLES.rubikSmall} color="rgba(1,1,1,.6)">
                    Cargando métodos de pago...
                </Typography>
            ) : (
                <>
                    <RadioGroup value={paymentOptionId ?? ""} onChange={onChange}>
                        <Stack spacing={1.25}>
                            {flatOptions.map((opt: any) => (
                                <PaymentOptionItem
                                    key={String(opt?.id ?? "")}
                                    value={String(opt?.id ?? "")}
                                    title={String(
                                        opt?.[OrderPaymentOptionViewDTOFields.OptionName] ?? "",
                                    )}
                                    description={String(
                                        opt?.[OrderPaymentOptionViewDTOFields.Description] ?? "",
                                    )}
                                    providerCode={Number(
                                        opt?.[OrderPaymentOptionViewDTOFields.ProviderCode] ?? 0,
                                    )}
                                    selected={paymentOptionId === String(opt?.id ?? "")}
                                />
                            ))}
                        </Stack>
                    </RadioGroup>

                    {selectedPaymentProviderCode === PROVEEDOR_PAGO.TARJETA && (
                        <CardPaymentForm
                            paymentData={paymentData}
                            setPaymentData={handleSetPaymentData}
                            cardTypes={cardTypes}
                            isLoadingCardTypes={isLoadingCardTypes}
                        />
                    )}

                    {selectedPaymentProviderCode === PROVEEDOR_PAGO.CBU && (
                        <CbuPaymentForm
                            paymentData={paymentData}
                            setPaymentData={handleSetPaymentData}
                        />
                    )}

                    {errorMsg && (
                        <Typography color="error" fontSize={12} mt={0.5}>
                            {errorMsg}
                        </Typography>
                    )}
                </>
            )}
        </Stack>
    );
};

interface PaymentDataFormProps {
    paymentData: OrderPaymentData | null;
    setPaymentData: React.Dispatch<React.SetStateAction<OrderPaymentData | null>>;
}

interface CardPaymentFormProps extends PaymentDataFormProps {
    cardTypes: CardTypeViewDTO[];
    isLoadingCardTypes: boolean;
}

const PaymentFormShell: React.FC<{ title: string; children: React.ReactNode }> = ({
    title,
    children,
}) => (
    <Box
        sx={{
            mt: 2,
            p: 2.5,
            borderRadius: "14px",
            border: "1.5px solid rgba(0,0,0,0.12)",
            borderLeft: "3px solid #1a1c1e",
            background: "rgba(0,0,0,0.015)",
        }}
    >
        <Typography
            sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#6b7280",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                mb: 2,
                fontFamily: "inherit",
            }}
        >
            {title}
        </Typography>
        {children}
    </Box>
);

// Agrupa dígitos en bloques de 4, adaptándose a cualquier longitud
const formatCardNumber = (raw: string, maxDigits: number): string => {
    const digits = raw.slice(0, maxDigits);
    return digits.replace(/(.{4})/g, "$1 ").trim();
};

// CBU Argentina: 8 dígitos (banco+sucursal) + 14 dígitos (cuenta+verificador)
const formatCbu = (raw: string): string => {
    if (raw.length <= 8) return raw;
    return `${raw.slice(0, 8)} ${raw.slice(8, 22)}`;
};

const CardPaymentForm: React.FC<CardPaymentFormProps> = ({
    paymentData,
    setPaymentData,
    cardTypes,
    isLoadingCardTypes,
}) => {
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const touch = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

    const update = (field: keyof OrderPaymentData, value: any) =>
        setPaymentData((prev) => ({
            ...(prev ?? { codPagoProveedor: PROVEEDOR_PAGO.TARJETA }),
            [field]: value,
        }));

    const selectedCardType = cardTypes.find((ct) => ct.id === paymentData?.codTarjetaTipo);
    const maxDigits = selectedCardType?.sinCantDigitos ?? 16;

    const bitMarcaNoCoincide =
        !!paymentData?.tarjetaNumero &&
        !!selectedCardType &&
        !cardNumberMatchesSelectedBrand(paymentData.tarjetaNumero, selectedCardType.descripcion);
    const marcaDetectada = detectCardBrand(paymentData?.tarjetaNumero);

    const errors = {
        tarjetaNombreTitular:
            touched.tarjetaNombreTitular && !paymentData?.tarjetaNombreTitular?.trim(),
        codTarjetaTipo: touched.codTarjetaTipo && !paymentData?.codTarjetaTipo,
        tarjetaNumero:
            (touched.tarjetaNumero && !paymentData?.tarjetaNumero) ||
            (touched.tarjetaNumero && bitMarcaNoCoincide),
        tarjetaFechaVto:
            touched.tarjetaFechaVto &&
            (!paymentData?.tarjetaFechaVto || paymentData.tarjetaFechaVto.length < 5),
    };

    return (
        <PaymentFormShell title="Datos de la tarjeta">
            <Stack spacing={1.5}>
                <TextField
                    label="Nombre del titular *"
                    value={paymentData?.tarjetaNombreTitular ?? ""}
                    onChange={(e) => update("tarjetaNombreTitular", e.target.value)}
                    onBlur={() => touch("tarjetaNombreTitular")}
                    error={errors.tarjetaNombreTitular}
                    helperText={errors.tarjetaNombreTitular ? "Requerido" : undefined}
                    fullWidth
                    sx={textFieldStyle}
                />
                <Box sx={{ display: "flex", gap: 1.5, flexDirection: { xs: "column", sm: "row" } }}>
                    <FormControl
                        disabled={isLoadingCardTypes}
                        error={errors.codTarjetaTipo}
                        sx={{ ...textFieldStyle, flex: { sm: "1 1 0" } }}
                    >
                        <InputLabel>Tipo *</InputLabel>
                        <Select
                            value={paymentData?.codTarjetaTipo ?? ""}
                            label="Tipo *"
                            onChange={(e) => update("codTarjetaTipo", Number(e.target.value))}
                            onBlur={() => touch("codTarjetaTipo")}
                        >
                            {cardTypes.map((ct) => (
                                <MenuItem key={ct.id} value={ct.id}>
                                    {ct.descripcion}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.codTarjetaTipo && (
                            <Typography
                                sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5, ml: 1.75 }}
                            >
                                Requerido
                            </Typography>
                        )}
                    </FormControl>
                    <TextField
                        label="Número *"
                        value={formatCardNumber(paymentData?.tarjetaNumero ?? "", maxDigits)}
                        onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, "").slice(0, maxDigits);
                            update("tarjetaNumero", digits);
                        }}
                        onBlur={() => touch("tarjetaNumero")}
                        error={errors.tarjetaNumero}
                        helperText={
                            touched.tarjetaNumero && !paymentData?.tarjetaNumero
                                ? "Requerido"
                                : touched.tarjetaNumero && bitMarcaNoCoincide
                                  ? `El número parece ser ${cardBrandLabel(marcaDetectada)}, no ${selectedCardType?.descripcion}`
                                  : `${maxDigits} dígitos`
                        }
                        inputProps={{ inputMode: "numeric" }}
                        sx={{ ...textFieldStyle, flex: { sm: "1.3 1 0" } }}
                    />
                    <TextField
                        label="Vto. (MM/AA) *"
                        value={paymentData?.tarjetaFechaVto ?? ""}
                        onChange={(e) => {
                            let val = e.target.value.replace(/[^0-9/]/g, "");
                            if (val.length === 2 && !val.includes("/")) val += "/";
                            update("tarjetaFechaVto", val.slice(0, 5));
                        }}
                        onBlur={() => touch("tarjetaFechaVto")}
                        error={errors.tarjetaFechaVto}
                        helperText={errors.tarjetaFechaVto ? "Formato MM/AA" : undefined}
                        placeholder="MM/AA"
                        sx={{ ...textFieldStyle, flex: { sm: "0 0 130px" } }}
                    />
                </Box>
            </Stack>
        </PaymentFormShell>
    );
};

const CbuPaymentForm: React.FC<PaymentDataFormProps> = ({ paymentData, setPaymentData }) => {
    const [touched, setTouched] = useState(false);
    const rawCbu = paymentData?.cbu ?? "";
    const hasError = touched && rawCbu.length < 22;

    return (
        <PaymentFormShell title="Datos bancarios">
            <TextField
                label="CBU *"
                value={formatCbu(rawCbu)}
                onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 22);
                    setPaymentData({ codPagoProveedor: PROVEEDOR_PAGO.CBU, cbu: digits });
                }}
                onBlur={() => setTouched(true)}
                error={hasError}
                helperText={
                    hasError ? "Ingresá los 22 dígitos del CBU" : `${rawCbu.length}/22 dígitos`
                }
                inputProps={{ inputMode: "numeric" }}
                fullWidth
                sx={textFieldStyle}
            />
        </PaymentFormShell>
    );
};

interface PaymentOptionItemProps {
    value: string;
    title: string;
    description: string;
    providerCode: number;
    selected: boolean;
}

const PaymentOptionItem: React.FC<PaymentOptionItemProps> = ({
    value,
    title,
    description,
    providerCode,
    selected,
}) => {
    const isMP = isMercadoPagoProvider(providerCode);
    const iconCfg = PROVIDER_ICON_CONFIG[providerCode] ?? PROVIDER_ICON_CONFIG[2];

    return (
        <Box
            sx={{
                border: "1.5px solid",
                borderColor: selected ? "#1a1c1e" : "rgba(0,0,0,0.12)",
                borderRadius: "14px",
                background: selected ? "rgba(26,28,30,0.025)" : "#ffffff",
                transition: "border-color 0.15s, background 0.15s",
                "&:hover": { borderColor: selected ? "#1a1c1e" : "rgba(0,0,0,0.28)" },
            }}
        >
            <FormControlLabel
                value={value}
                control={
                    <Radio
                        sx={{
                            color: "rgba(0,0,0,0.28)",
                            "&.Mui-checked": { color: "#1a1c1e" },
                            p: 1.5,
                        }}
                    />
                }
                sx={{
                    width: "100%",
                    m: 0,
                    pr: 1.5,
                    "& > .MuiFormControlLabel-label": { width: "100%", minWidth: 0 },
                }}
                label={
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={1.5}
                        py={1}
                        sx={{ width: "100%", minWidth: 0 }}
                    >
                        {/* Ícono del proveedor */}
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "10px",
                                bgcolor: iconCfg.bg,
                                color: iconCfg.color,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            {isMP ? (
                                <Box
                                    component="img"
                                    src={MP_LOGO_URL}
                                    alt="Mercado Pago"
                                    loading="lazy"
                                    sx={{ height: 22, width: "auto", display: "block" }}
                                />
                            ) : (
                                iconCfg.icon
                            )}
                        </Box>

                        {/* Nombre y descripción */}
                        <Stack flex={1} minWidth={0}>
                            <Typography
                                fontWeight={600}
                                sx={{
                                    color: "#1a1c1e",
                                    fontSize: "14px",
                                    fontFamily: "inherit",
                                    lineHeight: 1.3,
                                }}
                            >
                                {isMP ? "Mercado Pago" : title}
                            </Typography>
                            <Typography
                                sx={{
                                    color: "#6b7280",
                                    fontSize: "12px",
                                    fontFamily: "inherit",
                                    mt: 0.25,
                                    lineHeight: 1.4,
                                }}
                            >
                                {isMP ? "Tarjeta, cuotas y billetera virtual" : description}
                            </Typography>
                        </Stack>

                        {/* Check al seleccionar */}
                        {selected && (
                            <CheckCircle
                                size={20}
                                weight="fill"
                                color="#1a1c1e"
                                style={{ flexShrink: 0 }}
                            />
                        )}
                    </Stack>
                }
            />
        </Box>
    );
};
